import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Send, X, User, Building2, CheckCircle, AlertCircle, Globe, Sparkles, Calculator } from "lucide-react";
import { toast } from "sonner";
import { PhoneInput } from "../shared/PhoneInput";
import { CFCalculatorModal } from "./CFCalculatorModal";
import { CFPreviewPill } from "./CFPreviewPill";
import { getPrefixByCountry, getCountryByPrefix, getLocalizedCountryName, normalizeCountryCode } from "../../constants/countries";
import { validateFiscalCode, validateVat, getFiscalCodeInfo, getVatInfo } from "../../utils/fiscalCodeMulti";
import { validateVin } from "../../utils/vinValidator";
import { detectVinOrigin } from "../../utils/vinOrigin";
import { translateValidationError } from "../../utils/validationI18n";
import { getDealerFormI18n } from "../../translations/dealerCustomerForm";
import { serviceTypeLabel } from "../../utils/serviceTypeLabel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback dropdown options used when GET /api/service-types is unreachable
// (network blip, backend restart, etc). Must stay in sync with the server-side
// _ACTIVE_DURATIONS_BY_OEM table in services/service_type_display.py.
// CR Zorzutti 03/10/2026 — rename "LoJack Tracker Passive X" display labels
// to the OEM-specific "Track & Trace GPS X" naming. Internal key stays stable.
const SERVICE_TYPE_FALLBACK = [
  { value: "LoJack Tracker Passive 1", label: "Track & Trace GPS 1" },
  { value: "LoJack Tracker Passive 3", label: "Track & Trace GPS 3" },
  { value: "LoJack Tracker Passive 4", label: "Track & Trace GPS 4" },
];

// Countries supported by VIES (EU member states only).
const VIES_COUNTRIES = new Set([
  'IT','FR','ES','DE','PT','BE','NL','PL','SE','FI','DK','CZ','GR','AT','HU',
  'RO','SK','SI','HR','BG','EE','LT','LV','LU','MT','CY','IRL','IE',
]);

/**
 * Smart "New Customer" form used by Dealers / Sellers.
 *
 * Driven by `customer_type` (private/business):
 *   - private:  Nome + Cognome + Codice Fiscale
 *   - business: Ragione Sociale + P.IVA (+ VIES lookup auto-fills name/address)
 *
 * Fields activate progressively. Country is inherited from the dealer; the
 * user can override via a discreet "Cliente in altro paese?" link.
 *
 * VIN/Targa come before Brand/Model so we can auto-fill via NHTSA vPIC.
 */
const DealerCustomerForm = ({
  formData, setFormData, handleCreateCustomer, creating,
  showConfirm, setShowConfirm, countries, t, lang, dealerCountry = 'IT',
  onCancel,
  // 10/06/2026 — Zorzutti spec: the New Customer form now exposes a
  // "Seller credited" dropdown that is mandatory for Manager + Back-Office
  // creators (and pre-pinned to the seller themselves when a Seller is
  // the one filling the form). `dealerRole` drives the conditional
  // rendering; `dealerToken` is needed to fetch the dropdown options.
  dealerRole, dealerToken,
}) => {
  const navigate = useNavigate();
  // Wizard-specific translation pack (covers the ~30 new keys introduced by
  // the smart form). Falls back to the dealer's chosen UI language `lang`,
  // otherwise to the dealer's home country, otherwise IT.
  const wizardLang = (lang || dealerCountry || 'IT').toUpperCase();
  const tf = getDealerFormI18n(wizardLang);
  // Merged t-bag: wizard-specific keys + base dealer translations.
  const tt = { ...tf, ...t };

  // Snapshot dei dati iniziali del form per il bottone "Reset campi"
  // (req. Mara Capelli Stand-up 26/05/2026 punto 1c). Catturiamo il valore
  // di formData solo alla prima render, così il reset torna allo stato
  // pre-compilato dal Dealer (Email/Phone/CF/PIVA inseriti nella home).
  const initialFormDataRef = useRef(formData);
  const initialFormData = initialFormDataRef.current;

  const [customerType, setCustomerType] = useState(formData.customer_type || 'private');
  const [showCountryOverride, setShowCountryOverride] = useState(false);
  const [vinDecoding, setVinDecoding] = useState(false);
  const [vinSuggestion, setVinSuggestion] = useState(null);
  const [viesLoading, setViesLoading] = useState(false);
  const [viesResult, setViesResult] = useState(null);

  // ── Anti-typo double-confirmation state ─────────────────────────────
  // Req. Fabrizio 22/02/2026 — after several customers were created
  // with malformed email addresses ("gmial.com", "yaho.it", missing
  // digits in the phone number), the dealer form now requires the
  // dealer to retype both the email address AND the mobile number in
  // a second confirmation field before the "Salva" button unlocks.
  // Copy/paste is intentionally disabled on the confirmation fields
  // (option 2b) so the dealer is forced to type again and cannot
  // duplicate a typo present in the first field. Case-insensitive on
  // email and space-tolerant on phone: "MARIO@Gmail.COM" == "mario@gmail.com"
  // and "340 123 45 67" == "3401234567". The confirmation state is
  // wiped whenever the source field changes so a stale match cannot
  // silently keep the submit button enabled.
  const [emailConfirm, setEmailConfirm] = useState('');
  const [phoneConfirm, setPhoneConfirm] = useState('');
  // CF calculator modal (Fabrizio 27/07/2026 — "CF inversa" per ridurre
  // errori di battitura durante l'inserimento manuale del cliente).
  // Attivo solo per clienti IT + private (il CF non esiste in altri paesi).
  const [showCFCalc, setShowCFCalc] = useState(false);
  const normalizeEmail = (v) => (v || '').trim().toLowerCase();
  const normalizePhone = (v) => (v || '').replace(/\s+/g, '');
  const lastDecodedVinRef = useRef("");

  // Dropdown "Tipo Servizio" — OEM-aware labels loaded from backend
  // (/api/service-types). Falls back to the hardcoded Toyota pilot list
  // if the endpoint is unreachable, so the form never breaks.
  // CR Zorzutti 03/10/2026 — Track & Trace GPS rename.
  const [serviceTypeOptions, setServiceTypeOptions] = useState(SERVICE_TYPE_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/service-types`)
      .then(res => {
        if (cancelled) return;
        if (Array.isArray(res.data) && res.data.length > 0) {
          setServiceTypeOptions(res.data);
        }
      })
      .catch(() => {
        // Silent fallback: SERVICE_TYPE_FALLBACK already set, no UX impact.
      });
    return () => { cancelled = true; };
  }, []);

  // Persist the last 3 countries a dealer manually picked (excluding their
  // own dealer country, which is always the default). Stored in localStorage
  // so dealers serving multiple markets have their frequent picks 1 click away.
  const RECENT_COUNTRIES_KEY = 'lojack:dealer:recent_countries';
  const [recentCountries, setRecentCountries] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_COUNTRIES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(c => typeof c === 'string').slice(0, 3) : [];
    } catch {
      return [];
    }
  });

  const rememberCountry = (code) => {
    if (!code || code === dealerCountry) return;
    setRecentCountries(prev => {
      const next = [code, ...prev.filter(c => c !== code)].slice(0, 3);
      try { localStorage.setItem(RECENT_COUNTRIES_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // ─── Country-level "Required by law" policy ─────────────────────────
  //
  // Mara CR 20/02/2026 + Zorzutti — the IT Admin "Customer Communications →
  // Per-country Policy" panel exposes 3 checkboxes (Tax ID / Date of birth /
  // Place of birth) that mark which personal data fields are LEGALLY
  // required for each market. The dealer's customer-creation form must
  // honour that setting: if Admin unchecks "Tax ID required" for a given
  // country, the Codice Fiscale field for customers in that country
  // becomes OPTIONAL (no asterisk, no blocking on submit).
  //
  // We default to OPT-OUT (all 3 false) for two reasons:
  //   1. GDPR "minima necessaria" principle — don't ask if you don't know
  //   2. Cache hit from a previous form mount keeps the UX flash-free
  //
  // Per-country results are cached in localStorage so subsequent mounts
  // (the dealer creates 5 customers in a row) skip the fetch entirely.
  // Cache TTL is intentionally infinite — the admin policy doesn't
  // change every minute; a stale 1-day cache is still GDPR-safe vs.
  // the old hardcoded "always required" behaviour.
  const POLICY_CACHE_KEY = "lojack:country_policy:v1";
  const _readCachedPolicy = (cc) => {
    try {
      const raw = localStorage.getItem(POLICY_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      const entry = parsed && parsed[cc];
      if (entry && typeof entry === "object") return entry;
    } catch { /* corrupt cache → fall through to fetch */ }
    return null;
  };
  const _writeCachedPolicy = (cc, value) => {
    try {
      const raw = localStorage.getItem(POLICY_CACHE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[cc] = value;
      localStorage.setItem(POLICY_CACHE_KEY, JSON.stringify(parsed));
    } catch { /* ignore quota */ }
  };
  const [countryPolicy, setCountryPolicy] = useState(() => {
    const cc0 = (formData.country || dealerCountry || "IT").toUpperCase();
    return _readCachedPolicy(cc0) || { tax_code: false, birth_date: false, birth_place: false };
  });

  // On mount, ensure formData.country reflects dealer country (override hidden)
  useEffect(() => {
    if (!formData.country || formData.country !== dealerCountry) {
      setFormData(prev => ({
        ...prev,
        country: dealerCountry,
        phone_prefix: getPrefixByCountry(dealerCountry),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerCountry]);

  const country = (formData.country || dealerCountry || 'IT').toUpperCase();
  const cfInfo = getFiscalCodeInfo(country);
  const vatInfo = getVatInfo(country);

  // Refresh the country policy whenever the customer's country changes.
  // First we hydrate from the localStorage cache (instant, no flash),
  // then we fetch fresh from the backend to pick up any admin update.
  useEffect(() => {
    if (!country) return;
    const cached = _readCachedPolicy(country);
    if (cached) setCountryPolicy(cached);
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/country-config`);
        const entry = (res.data?.countries || []).find(
          c => c.code === country || c.country === country
        );
        if (cancelled || !entry?.required_fields) return;
        const next = {
          tax_code: Boolean(entry.required_fields.tax_code),
          birth_date: Boolean(entry.required_fields.birth_date),
          birth_place: Boolean(entry.required_fields.birth_place),
        };
        setCountryPolicy(next);
        _writeCachedPolicy(country, next);
      } catch (_) { /* keep last-known-good policy on network error */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  // Tax Code is "required" only when (a) the customer is a private individual
  // AND (b) the country policy marks it as legally required. Business
  // customers always use VAT instead, so tax_code is never required for them.
  const taxCodeIsRequired = customerType === 'private' && countryPolicy.tax_code;

  // ─── Bilingual document labels ────────────────────────────────────────
  // When the dealer's UI language differs from the customer's country,
  // we render the field label as "<UI-generic name> (<country-specific
  // native name>)" so the dealer recognises BOTH the universal concept
  // (Tax Code / VAT Number) AND the exact document required for that
  // country (Codice Fiscale / PESEL / Steuer-ID / …). When they match,
  // we keep just the native name to avoid pointless redundancy.
  const docLabel = (uiLabel, nativeLabel) => {
    if (!nativeLabel) return uiLabel;
    if (!uiLabel || uiLabel === nativeLabel) return nativeLabel;
    return `${uiLabel} (${nativeLabel})`;
  };
  const taxCodeFullLabel = docLabel(tt.taxCodeUiLabel, cfInfo.label);
  const vatNumberFullLabel = docLabel(tt.vatNumberUiLabel, vatInfo.label);
  // Localised "e.g." prefix for placeholders.
  const egPrefix = { IT: 'es.', FR: 'ex.', BE: 'ex.', ES: 'ej.', PT: 'ex.',
                     DE: 'z.B.', CH: 'z.B.', NL: 'bv.', PL: 'np.',
                     SE: 't.ex.', NO: 'f.eks.', FI: 'esim.', DK: 'f.eks.',
                     CZ: 'napr.', GR: 'p.ch.',
                     UK: 'e.g.', GBZ: 'e.g.', IRL: 'e.g.' }[wizardLang] || 'e.g.';

  // ─── Progressive enable rules ─────────────────────────────────────────
  // Each step is gated by the previous one. Disabled inputs are still
  // visible (greyed out) per UX choice "a1".
  const hasPhone = Boolean(formData.phone && (formData.phone || '').trim().length >= 6);
  // hasIdentity also gates the "next" steps (email/VIN/vehicle). Tax code
  // is only required when the country policy marks it as legally required;
  // when Admin un-checks the box, the dealer can proceed with just
  // first_name + last_name and the tax_code stays optional.
  const hasIdentity = customerType === 'private'
    ? Boolean(
        formData.first_name && formData.last_name &&
        (!taxCodeIsRequired || formData.tax_code)
      )
    : Boolean(formData.company_name && formData.vat_number);
  const hasEmail = Boolean(formData.email && /\S+@\S+\.\S+/.test(formData.email));

  // Anti-typo confirmation matches (Req. Fabrizio 22/02/2026).
  // Both booleans gate `enableSubmit` below so the dealer literally
  // cannot save the customer until they retype the same email + phone.
  const emailMatches = hasEmail && normalizeEmail(emailConfirm) === normalizeEmail(formData.email);
  const phoneMatches = hasPhone && normalizePhone(phoneConfirm) === normalizePhone(formData.phone);
  // VIN origin detection (WMI lookup): runs as soon as we have at least 3
  // chars. Shown as a neutral informational hint to the dealer
  // ("Veicolo: Francia · Renault"). We intentionally do NOT compare the
  // vehicle's manufacturing country with the customer's country anymore:
  // imported cars are the norm in the European aftermarket — a Polish
  // customer routinely owns a French Renault, an Italian a German Audi,
  // etc. Flagging this as a mismatch was a false positive that confused
  // dealers (Fabrizio request 02/06/2026). The WMI hint stays because
  // it's still useful as a sanity check ("oh I typed the wrong VIN
  // entirely — this says it's an American car, can't be right").
  const vinOrigin = (formData.vehicle_vin && formData.vehicle_vin.length >= 3)
    ? detectVinOrigin(formData.vehicle_vin)
    : null;

  // VIN validation result. Considered "valid" for gating purposes when:
  //   - the VIN is 17 chars AND
  //   - severity is 'ok' (checksum ok) OR
  //   - severity is 'warn' but only for non-NA checksum mismatch (acceptable
  //     because European VINs do not strictly enforce the SAE J272 check digit).
  // SUSPICIOUS_PATTERN, BAD_LENGTH, FORBIDDEN_CHAR, CHECKSUM_FAIL (for NA) all block.
  const vinValidation = (formData.vehicle_vin && formData.vehicle_vin.length === 17)
    ? validateVin(formData.vehicle_vin)
    : null;
  const vinAcceptable = vinValidation && vinValidation.valid;
  const hasVin = Boolean(vinAcceptable);
  // hasVehicle (req. Zorzutti 22/05/2026): VRN is now OPTIONAL, so only
  // brand+model are required to consider the vehicle data complete.
  const hasVehicle = Boolean(formData.vehicle_brand && formData.vehicle_model);

  // Field gating — STEP ORDER (req. Zorzutti briefing 22/05/2026):
  //   1) Customer Type (always enabled, must be picked first)
  //   2) Phone   (enabled after type is selected)
  //   3) Identity (enabled after phone is OK)
  //   4) Email
  //   5) VIN, Vehicle, Service
  const enableType = true;
  const enablePhone = Boolean(customerType);
  const enableIdentity = enablePhone && hasPhone;
  const enableEmail = hasIdentity;
  const enableVin = hasEmail;
  const enableVehicle = hasVin || (formData.vehicle_vin && formData.vehicle_vin.length >= 11);
  const enableService = hasVehicle;
  // 10/06/2026 — Zorzutti: a Manager / Back-Office cannot submit until
  // they pick a credited Seller. A regular Seller auto-attributes the
  // customer to themselves so the field is bypassed.
  const sellerCreditedOk = dealerRole === 'seller' || Boolean(formData.assigned_seller_id);
  const enableSubmit = hasPhone && phoneMatches && hasIdentity && hasEmail && emailMatches && hasVin && hasVehicle && formData.service_type && sellerCreditedOk;

  // CF / VAT validation
  const taxRes = (customerType === 'private' && formData.tax_code)
    ? validateFiscalCode(formData.tax_code, country) : { valid: true };
  const vatRes = (customerType === 'business' && formData.vat_number)
    ? validateVat(formData.vat_number, country) : { valid: true };

  // VIN-character normalisation map. Several real-world VINs picked up
  // via OCR / PDF copy-paste / mobile auto-correct contain Unicode
  // look-alikes (e.g. ``Ø`` for ``0``) that the strict ISO-3779
  // validator correctly rejects — but the dealer is left blocked
  // without knowing what to fix. We auto-substitute the most common
  // lookalikes and surface a friendly toast explaining what we did.
  // Keep this table in sync with /app/backend/services/vin_normaliser.py.
  const VIN_LOOKALIKE_MAP = {
    "Ø": "0", "ø": "0", "Ǿ": "0", "ǿ": "0",
    "０": "0", "１": "1", "２": "2", "３": "3", "４": "4",
    "５": "5", "６": "6", "７": "7", "８": "8", "９": "9",
    // Cyrillic look-alikes of common Latin letters
    "А": "A", "В": "B", "Е": "E", "К": "K", "М": "M",
    "Н": "H", "О": "O", "Р": "P", "С": "C", "Т": "T",
    "Х": "X", "У": "Y",
  };
  const normaliseVin = (raw) => {
    if (!raw) return { canonical: "", replacements: [] };
    const stripped = String(raw).replace(/[\s\u00a0\-_]/g, "");
    const replacements = [];
    let canonical = "";
    for (const ch of stripped) {
      const remap = VIN_LOOKALIKE_MAP[ch];
      if (remap) {
        canonical += remap;
        replacements.push({ from: ch, to: remap });
      } else {
        canonical += ch;
      }
    }
    return { canonical: canonical.toUpperCase(), replacements };
  };

  // ─── VIN auto-decode (offline WMI first, NHTSA only as fallback) ─────
  // Strategy after the 30/05/2026 audit:
  //  1. As soon as we have ≥3 chars, look up the WMI offline (100%
  //     coverage for EU manufacturers, 0 latency, 0 cost).
  //  2. Only if it's NOT a European WMI and we have a full 17-char
  //     VIN, fall back to NHTSA for an extra make/model suggestion.
  //  3. If neither works, surface a SOFT informational toast — never
  //     blocking, never amber-coloured.
  // Chiarimento Zorzutti (24/05/2026, post-meeting): la frase del PDF del
  // 22/05 "Non riempie in automatico i campi MARCA e MODELLO" era una
  // SEGNALAZIONE DI BUG, non un'istruzione di disabilitare l'auto-fill.
  // Comportamento corretto: a 17 caratteri VIN il sistema decodifica via
  // NHTSA e POPOLA AUTOMATICAMENTE Marca + Modello, mostrando un banner
  // di conferma informativo (no bottoni). I campi restano editabili dal
  // dealer in caso di necessità di correzione manuale.
  const decodeVin = async (vin) => {
    if (!vin || vin === lastDecodedVinRef.current) return;
    lastDecodedVinRef.current = vin;

    // Step 1 — Always try the offline WMI decoder first. It's free
    // and covers 100% of European manufacturers. Even a partial VIN
    // (≥3 chars) is enough to recognise the manufacturer.
    let wmiHit = null;
    if (vin.length >= 3) {
      try {
        const wmiRes = await axios.get(`${API}/vin/decode-wmi/${encodeURIComponent(vin)}`);
        wmiHit = wmiRes.data || null;
        if (wmiHit && wmiHit.manufacturer && !formData.vehicle_brand) {
          // Auto-fill the brand silently (the country chip below will
          // show the country flag, so the dealer sees what we did).
          setFormData(prev => ({
            ...prev,
            vehicle_brand: prev.vehicle_brand || wmiHit.manufacturer,
          }));
        }
        // If the WMI decoder also extracted the model year via the
        // ISO 3779 position-10 rule, surface it in the suggestion
        // panel so the dealer can see it (the year input itself is
        // not in this form yet — adding it would force a layout
        // change; the suggestion banner is enough for now).
        if (wmiHit && wmiHit.year) {
          setVinSuggestion(prev => ({
            ...(prev || {}),
            make: prev?.make || wmiHit.manufacturer || '',
            year: String(wmiHit.year),
            auto_applied: true,
          }));
        }
      } catch {
        // 404 = WMI not in our offline dataset; not an error per se.
      }
    }

    // Step 2 — If we have a full VIN AND the WMI is NOT European,
    // fall back to NHTSA for the model suggestion. We skip the NHTSA
    // round-trip for EU VINs because NHTSA misses most of them and
    // we'd just slow down the dealer for no value.
    if (vin.length !== 17) return;
    const isEU = wmiHit && wmiHit.country_code && ["IT","FR","DE","ES","PT","NL","BE","LU","AT","PL","CZ","SK","HU","RO","BG","SI","HR","GR","SE","NO","FI","DK","EE","LT","LV","MT","CY","GBZ","IRL","CH"].includes(wmiHit.country_code);
    if (isEU) {
      // We've already filled the brand from the WMI; nothing else
      // to do. Don't bother NHTSA — its model coverage for EU VINs
      // is too unreliable to be useful here.
      return;
    }
    setVinDecoding(true);
    setVinSuggestion(null);
    try {
      const res = await axios.get(`${API}/vin/decode/${encodeURIComponent(vin.toUpperCase())}`);
      const d = res.data || {};
      if ((d.make || d.model) && !d.error_text) {
        setFormData(prev => ({
          ...prev,
          vehicle_brand: prev.vehicle_brand || d.make,
          vehicle_model: prev.vehicle_model || d.model,
        }));
        setVinSuggestion({
          make: d.make || '',
          model: d.model || '',
          year: d.year || '',
          auto_applied: true,
        });
        toast.success(`${tt.vinApplied || 'VIN riconosciuto'}: ${[d.make, d.model].filter(Boolean).join(' ')}`);
      }
      // No `else` branch: we deliberately DO NOT show "vin not
      // recognised" toasts anymore. The dealer can already see
      // empty brand+model fields and will fill them manually.
    } catch (err) {
      if (err.response?.status >= 500) {
        // Only surface 5xx (real outage). 404 from NHTSA = unknown
        // VIN = the dealer fills the form manually, that's fine.
        toast.error(tt.vinDecodeFailed);
      }
    } finally {
      setVinDecoding(false);
    }
  };

  const applyVinSuggestion = () => {
    if (!vinSuggestion) return;
    setFormData(prev => ({
      ...prev,
      vehicle_brand: vinSuggestion.make || prev.vehicle_brand,
      vehicle_model: vinSuggestion.model || prev.vehicle_model,
    }));
    setVinSuggestion(null);
    toast.success(tt.vinApplied);
  };

  // ─── VAT prefix → country auto-detection (Fix B — Bug Zorzutti) ──────
  // Map of VIES VAT prefixes → application country codes.
  const VAT_PREFIX_TO_COUNTRY = {
    IT: 'IT', FR: 'FR', ES: 'ES', DE: 'DE', GB: 'UK', PT: 'PT',
    NL: 'NL', BE: 'BE', PL: 'PL', SE: 'SE', NO: 'NO', FI: 'FI',
    DK: 'DK', CZ: 'CZ', EL: 'GR', GI: 'GBZ', IE: 'IRL',
  };
  const detectCountryFromVat = (vat) => {
    const raw = (vat || '').trim().toUpperCase();
    if (raw.length < 3) return null;
    // Special case: Switzerland uses 3-letter prefix "CHE"
    if (raw.startsWith('CHE')) return 'CH';
    const prefix2 = raw.slice(0, 2);
    if (!/^[A-Z]{2}$/.test(prefix2)) return null;
    return VAT_PREFIX_TO_COUNTRY[prefix2] || null;
  };
  const vatDetectedCountry = customerType === 'business'
    ? detectCountryFromVat(formData.vat_number) : null;
  const vatCountryMismatch = vatDetectedCountry && vatDetectedCountry !== country;

  // ─── Fix D (Bug Zorzutti — privato estero) ───────────────────────────
  // For PRIVATE customers there's no VAT prefix to lean on. We surface
  // the same "country mismatch" banner using the phone dial-prefix as a
  // soft signal. The banner is purely advisory: clicking the CTA aligns
  // the customer country to the phone-derived country in one tap.
  // We DO NOT auto-switch silently nor enforce server-side, because a
  // phone prefix is a commercial datum that can legitimately differ
  // (e.g. an Italian travelling with a foreign SIM).
  const phoneDetectedCountry = (formData.phone_prefix && (formData.phone || '').length >= 6)
    ? getCountryByPrefix(formData.phone_prefix)
    : null;
  // Fabrizio bug 06/07/2026 — Il DB salva ``GB``/``IE``/``GI`` (ISO)
  // mentre ``getCountryByPrefix`` ritorna i codici legacy ``UK``/``IRL``/``GBZ``.
  // Confrontarli direttamente produceva un falso positivo "Number from
  // another country detected" per i dealer inglesi/irlandesi/gibraltar.
  // Normalizziamo entrambi i lati alla forma legacy prima del confronto.
  const phoneCountryMismatch =
    phoneDetectedCountry
    && normalizeCountryCode(phoneDetectedCountry) !== normalizeCountryCode(country)
    && !vatCountryMismatch;

  // ─── VIES VAT lookup ──────────────────────────────────────────────────
  const handleViesLookup = async () => {
    const vatRaw = (formData.vat_number || '').trim();
    if (!vatRaw) { toast.error(tt.vatRequiredFirst); return; }
    if (!VIES_COUNTRIES.has(country)) {
      toast.error(tt.viesNotSupported);
      return;
    }
    setViesLoading(true);
    setViesResult(null);
    try {
      const res = await axios.get(`${API}/vies/lookup`, { params: { country, vat: vatRaw } });
      setViesResult(res.data);
      toast.success(tt.viesFound);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) toast.error(tt.viesNotFound);
      else if (status === 400) toast.error(tt.viesNotSupported);
      else if (status === 502) toast.error(tt.viesUnavailable);
      else toast.error(err.response?.data?.detail || tt.viesError);
    } finally {
      setViesLoading(false);
    }
  };

  const applyViesData = () => {
    if (!viesResult) return;
    // Detect the country from VIES result; fallback to VAT prefix.
    const viesCountry = (viesResult.country_code || detectCountryFromVat(formData.vat_number));
    const targetCountry = viesCountry && VAT_PREFIX_TO_COUNTRY[viesCountry]
      ? VAT_PREFIX_TO_COUNTRY[viesCountry]
      : viesCountry;
    setFormData(prev => ({
      ...prev,
      company_name: viesResult.name || prev.company_name,
      // Fix B: if VIES knows the country, align the customer country to it.
      ...(targetCountry && targetCountry !== prev.country ? {
        country: targetCountry,
        phone_prefix: getPrefixByCountry(targetCountry),
      } : {}),
    }));
    if (targetCountry && targetCountry !== country) {
      rememberCountry(targetCountry);
      toast.success(tt.viesApplied + ` → ${targetCountry}`);
    } else {
      toast.success(tt.viesApplied);
    }
    setViesResult(null);
  };

  // ─── Customer type switch ─────────────────────────────────────────────
  const switchType = (type) => {
    setCustomerType(type);
    setFormData(prev => ({
      ...prev,
      customer_type: type,
      // Reset the "other side" fields when switching to avoid stale data
      ...(type === 'private'
        ? { company_name: '', vat_number: '' }
        : { last_name: '', tax_code: '' }),
    }));
  };

  // ─── Country override toggle ──────────────────────────────────────────
  // Controlled by the country banner at the top of the form. When `showCountryOverride`
  // is true, the banner switches to an inline dropdown to select a different country.

  // Helpers for input classes
  const inputCls = (enabled, valid = true) =>
    `w-full mt-1 px-4 py-3 bg-[#111] border ${
      !enabled ? 'border-gray-800 opacity-50 cursor-not-allowed' :
      !valid ? 'border-red-600' : 'border-gray-700'
    } rounded-xl text-white focus:border-lojack-red focus:outline-none disabled:cursor-not-allowed`;

  const sectionCls = (enabled) => enabled ? '' : 'opacity-50 pointer-events-none';

  return (
    <>
      <div className="max-w-lg mx-auto animate-fade-in">
        <div className="wallet-card">
          <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
            <Plus size={20} className="text-lojack-red" /> {t.newCustomer}
          </h2>
          <p className="text-gray-500 text-xs mb-4">{tt.dealerFormSubtitle}</p>

          {/* Country Banner — always-visible reminder of which country you're
              creating a customer in. The banner is read-only: the customer
              country is inherited from the dealer's home country and cannot
              be changed manually (req. Zorzutti briefing 22/05/2026).
              Cross-border cases (e.g. Italian dealer registering a Polish
              company) are still handled automatically by the VAT/phone
              mismatch banners further down (Fix B/D). */}
          {(() => {
            const currentCountry = countries.find(c => c.code === country) || { code: country, name: country, flag: '🌍' };
            return (
              <div className="mb-5 p-3 bg-gradient-to-r from-blue-950/30 to-transparent border border-blue-500/30 rounded-xl" data-testid="dealer-country-banner">
                <div className="flex items-center gap-2 min-w-0 animate-country-swap" key={`country-banner-${country}`}>
                  <Globe size={16} className="text-blue-400 flex-shrink-0" />
                  <span className="text-gray-400 text-xs">{tt.creatingCustomerIn}</span>
                  <span className="text-base">{currentCountry.flag}</span>
                  <span className="text-white text-sm font-semibold" data-testid="dealer-country-banner-label">{currentCountry.name}</span>
                </div>
              </div>
            );
          })()}

          <form onSubmit={handleCreateCustomer} className="space-y-5">

            {/* ── STEP 1: Customer Type ──────────────────────────────── */}
            <div className={sectionCls(enableType)}>
              <label className="text-gray-400 text-xs flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lojack-red text-white text-[10px] font-bold">1</span>
                {tt.customerType}*
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => enableType && switchType('private')}
                  disabled={!enableType}
                  className={`px-4 py-3 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                    customerType === 'private' ? 'border-lojack-red bg-lojack-red/10 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                  } disabled:cursor-not-allowed`}
                  data-testid="dealer-type-private"
                >
                  <User size={18} />
                  <span className="font-medium">{tt.private}</span>
                </button>
                <button
                  type="button"
                  onClick={() => enableType && switchType('business')}
                  disabled={!enableType}
                  className={`px-4 py-3 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                    customerType === 'business' ? 'border-lojack-red bg-lojack-red/10 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                  } disabled:cursor-not-allowed`}
                  data-testid="dealer-type-business"
                >
                  <Building2 size={18} />
                  <span className="font-medium">{tt.business}</span>
                </button>
              </div>
            </div>

            {/* ── STEP 2: Phone ──────────────────────────────────────── */}
            <div className={sectionCls(enablePhone)}>
              <label className="text-gray-400 text-xs flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lojack-red text-white text-[10px] font-bold">2</span>
                {t.phone}*
              </label>
              <div className="mt-1">
                <PhoneInput
                  prefix={formData.phone_prefix || getPrefixByCountry(country)}
                  number={formData.phone}
                  onChange={({ prefix, number }) => {
                    // Wipe the confirmation field whenever the primary
                    // phone changes so a stale match can never keep the
                    // submit button silently enabled.
                    if (number !== formData.phone) setPhoneConfirm('');
                    setFormData({ ...formData, phone_prefix: prefix, phone: number });
                  }}
                  required
                  testId="dealer-input-phone"
                  disabled={!enablePhone}
                  customerCountry={country}
                  dealerCountry={dealerCountry}
                  uiLang={lang}
                />
              </div>

              {/* Phone confirmation (anti-typo, Req. Fabrizio 22/02/2026).
                  Paste is intentionally blocked so the dealer must retype
                  the number — this makes it statistically impossible to
                  duplicate a typo. Field is disabled until the primary
                  phone has a plausible length. */}
              <div className="mt-2">
                <label className="text-gray-400 text-[11px] flex items-center gap-1.5">
                  <span className="text-lojack-red">↺</span>
                  {(tt.phoneConfirmLabel) || 'Conferma cellulare (ridigita)*'}
                </label>
                <input
                  type="tel"
                  value={phoneConfirm}
                  onChange={(e) => setPhoneConfirm(e.target.value.replace(/[^\d\s]/g, ''))}
                  onPaste={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  autoComplete="off"
                  disabled={!hasPhone}
                  placeholder={(tt.phoneConfirmPlaceholder) || 'Ridigita il numero'}
                  className={`w-full mt-1 px-4 py-2.5 bg-[#111] border rounded-xl text-white focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed text-sm ${
                    !hasPhone || !phoneConfirm ? 'border-gray-700' :
                    phoneMatches ? 'border-emerald-500 focus:border-emerald-400' :
                                   'border-red-500 focus:border-red-400'
                  }`}
                  data-testid="dealer-input-phone-confirm"
                />
                {hasPhone && phoneConfirm && (
                  <p className={`mt-1 text-[11px] flex items-center gap-1.5 ${
                    phoneMatches ? 'text-emerald-400' : 'text-red-400'
                  }`} data-testid="dealer-phone-confirm-feedback">
                    {phoneMatches
                      ? ((tt.phoneConfirmOk) || '✓ I numeri corrispondono')
                      : ((tt.phoneConfirmMismatch) || '⚠ I numeri non corrispondono')}
                  </p>
                )}
              </div>
              {/* Fix D (Bug Zorzutti — privato estero):
                  When the phone dial-prefix indicates a different country
                  than the customer's current one, surface a one-click
                  banner so the dealer can align the country to the
                  phone-derived one. Same UX pattern as the VAT mismatch
                  banner used for business customers. */}
              {phoneCountryMismatch && (
                <div className="mt-2 p-3 bg-lojack-clause-950/30 border border-lojack-clause-500/40 rounded-xl flex items-start gap-3"
                     data-testid="dealer-phone-country-mismatch">
                  <AlertCircle className="text-lojack-clause-400 flex-shrink-0 mt-0.5" size={18} />
                  <div className="flex-1">
                    <p className="text-lojack-clause-300 text-xs font-semibold mb-1">
                      {tt.phoneCountryMismatchTitle || 'Numero di un altro paese rilevato'}
                    </p>
                    <p className="text-gray-300 text-[11px] leading-relaxed">
                      {(tt.phoneCountryMismatchBody || 'Il prefisso telefonico indica un cliente {detected}, ma il paese del cliente è attualmente {current}. Vuoi aggiornarlo?')
                        .replace('{detected}', (countries.find(c => c.code === phoneDetectedCountry)?.name) || phoneDetectedCountry)
                        .replace('{current}', (countries.find(c => c.code === country)?.name) || country)}
                    </p>
                    <button type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                country: phoneDetectedCountry,
                                // keep the phone_prefix as the dealer typed it
                              }));
                              rememberCountry(phoneDetectedCountry);
                              toast.success(`${tt.phoneCountrySwitchedTo || 'Paese cliente aggiornato:'} ${(countries.find(c => c.code === phoneDetectedCountry)?.flag) || ''} ${(countries.find(c => c.code === phoneDetectedCountry)?.name) || phoneDetectedCountry}`);
                            }}
                            className="mt-2 px-3 py-1.5 bg-lojack-clause-600 hover:bg-lojack-clause-700 rounded-lg text-white text-[11px] font-medium transition-colors"
                            data-testid="dealer-phone-country-fix">
                      {(tt.phoneCountrySwitchTo || 'Imposta paese su {detected}').replace('{detected}', (countries.find(c => c.code === phoneDetectedCountry)?.flag || '') + ' ' + ((countries.find(c => c.code === phoneDetectedCountry)?.name) || phoneDetectedCountry))}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── STEP 3: Identity (Private OR Business) ────────────── */}
            <div className={sectionCls(enableIdentity)}>
              <label className="text-gray-400 text-xs flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lojack-red text-white text-[10px] font-bold">3</span>
                {customerType === 'private' ? (tt.privateIdentity) : (tt.businessIdentity)}*
              </label>

              {customerType === 'private' ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-gray-400 text-xs">{t.firstName}*</label>
                      <input
                        value={formData.first_name}
                        onChange={e => setFormData({...formData, first_name: e.target.value})}
                        disabled={!enableIdentity}
                        className={inputCls(enableIdentity)}
                        required={customerType === 'private'}
                        data-testid="dealer-input-first-name"
                      />
                    </div>
                    <div>
                      <label className="text-gray-400 text-xs">{t.lastName}*</label>
                      <input
                        value={formData.last_name}
                        onChange={e => setFormData({...formData, last_name: e.target.value})}
                        /* Mara Capelli feedback 12/02/2026: cognome
                           deve abilitarsi solo DOPO che il nome è
                           stato compilato. Riduce errori da utenti
                           che riempiono i campi out-of-order. */
                        disabled={!enableIdentity || !formData.first_name.trim()}
                        className={inputCls(enableIdentity && Boolean(formData.first_name.trim()))}
                        required={customerType === 'private'}
                        data-testid="dealer-input-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between animate-country-swap" key={`tc-lbl-${country}`}>
                      <label className="text-gray-400 text-xs">
                        {taxCodeFullLabel}{taxCodeIsRequired ? '*' : ''}
                      </label>
                      {/* "Calcola CF" — only for Italian private customers.
                          Opens a modal that computes the Codice Fiscale
                          from the client's identity fields. The generated
                          value is labelled "indicativo" — seller must
                          verify against the tessera sanitaria. */}
                      {country === 'IT' && customerType === 'private' && enableIdentity && dealerToken && (
                        <button
                          type="button"
                          onClick={() => setShowCFCalc(true)}
                          className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-lojack-red/10 hover:bg-lojack-red/20 border border-lojack-red/30 text-lojack-red hover:text-red-300 transition-colors"
                          data-testid="dealer-open-cf-calculator"
                          title={tt.cfCalcOpenTitle || 'Calcola il Codice Fiscale a partire dai dati anagrafici'}
                        >
                          <Calculator size={11} />
                          <span className="font-medium">{tt.cfCalcOpen || 'Calcola CF'}</span>
                        </button>
                      )}
                    </div>
                    <input
                      value={formData.tax_code || ''}
                      onChange={e => setFormData({...formData, tax_code: e.target.value.toUpperCase()})}
                      disabled={!enableIdentity}
                      placeholder={`${egPrefix} ${cfInfo.example}`}
                      key={`tc-input-${country}`}
                      className={`${inputCls(enableIdentity, taxRes.valid)} uppercase`}
                      required={taxCodeIsRequired}
                      data-testid="dealer-input-tax-code"
                    />
                    {/* "As-you-type" decode preview pill: shows identity
                        info derived from the CF for a silent visual
                        cross-check. Yellow badge if surname/name in the
                        CF don't match the plaintext values above. Same
                        gating as the Calcola CF button. Req. Fabrizio
                        27/07/2026. */}
                    {country === 'IT' && customerType === 'private' && enableIdentity && dealerToken && (
                      <CFPreviewPill
                        taxCode={formData.tax_code || ''}
                        firstName={formData.first_name || ''}
                        lastName={formData.last_name || ''}
                        dealerToken={dealerToken}
                        lang={wizardLang}
                      />
                    )}
                    {!formData.tax_code && enableIdentity && (
                      <p className="text-gray-500 text-[11px] mt-1 flex items-center gap-1 animate-country-swap" key={`tc-hint-${country}`} data-testid="dealer-tax-code-hint">
                        <Globe size={10} className="text-blue-400" /> {tt.formatHint}: <span className="font-mono text-gray-400">{cfInfo.example}</span>
                      </p>
                    )}
                    {formData.tax_code && !taxRes.valid && (
                      <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {translateValidationError(taxRes.error, wizardLang)}
                      </p>
                    )}
                    {formData.tax_code && taxRes.valid && (
                      <p className="text-lojack-blue-500 text-xs mt-1 flex items-center gap-1">
                        <CheckCircle size={12} /> {taxCodeFullLabel} {tt.validSuffix}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-400 text-xs">{tt.companyName}*</label>
                    <input
                      value={formData.company_name || ''}
                      onChange={e => setFormData({...formData, company_name: e.target.value})}
                      disabled={!enableIdentity}
                      className={inputCls(enableIdentity)}
                      required={customerType === 'business'}
                      data-testid="dealer-input-company-name"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs animate-country-swap" key={`vat-lbl-${country}`}>{vatNumberFullLabel}*</label>
                    <div className="flex gap-2 mt-1">
                      <input
                        value={formData.vat_number || ''}
                        onChange={e => setFormData({...formData, vat_number: e.target.value.toUpperCase()})}
                        disabled={!enableIdentity}
                        placeholder={`${egPrefix} ${vatInfo.example}`}
                        key={`vat-input-${country}`}
                        className={`flex-1 px-4 py-3 bg-[#111] border ${
                          !enableIdentity ? 'border-gray-800 opacity-50' :
                          !vatRes.valid ? 'border-red-600' : 'border-gray-700'
                        } rounded-xl text-white focus:border-lojack-red focus:outline-none uppercase disabled:cursor-not-allowed`}
                        required={customerType === 'business'}
                        data-testid="dealer-input-vat-number"
                      />
                      {VIES_COUNTRIES.has(country) && formData.vat_number && vatRes.valid && (
                        <button
                          type="button"
                          onClick={handleViesLookup}
                          disabled={viesLoading || !enableIdentity}
                          className="px-3 py-3 bg-blue-600/10 border border-blue-500/40 rounded-xl text-blue-400 text-xs font-medium hover:bg-blue-600/20 transition-colors disabled:opacity-50 whitespace-nowrap"
                          data-testid="dealer-vies-lookup-btn"
                          title={tt.viesTooltip}
                        >
                          {viesLoading ? '...' : 'VIES'}
                        </button>
                      )}
                    </div>
                    {!formData.vat_number && enableIdentity && (
                      <p className="text-gray-500 text-[11px] mt-1 flex items-center gap-1" data-testid="dealer-vat-hint">
                        <Globe size={10} className="text-blue-400" /> {tt.formatHint}: <span className="font-mono text-gray-400">{vatInfo.example}</span>
                      </p>
                    )}
                    {formData.vat_number && !vatRes.valid && (
                      <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {translateValidationError(vatRes.error, wizardLang)}
                      </p>
                    )}
                    {formData.vat_number && vatRes.valid && (
                      <p className="text-lojack-blue-500 text-xs mt-1 flex items-center gap-1">
                        <CheckCircle size={12} /> {vatInfo.label} valida
                      </p>
                    )}
                    {/* Fix B (Bug Zorzutti — azienda polacca):
                        If the VAT prefix indicates a different country than
                        the one currently selected (e.g. PL... while form is
                        on IT), surface a one-click banner so the dealer can
                        align the customer record to the real country. */}
                    {vatCountryMismatch && (
                      <div className="mt-2 p-3 bg-lojack-clause-950/30 border border-lojack-clause-500/40 rounded-xl flex items-start gap-3"
                           data-testid="dealer-vat-country-mismatch">
                        <AlertCircle className="text-lojack-clause-400 flex-shrink-0 mt-0.5" size={18} />
                        <div className="flex-1">
                          <p className="text-lojack-clause-300 text-xs font-semibold mb-1">
                            {tt.vatCountryMismatchTitle || 'P.IVA di un altro paese rilevata'}
                          </p>
                          <p className="text-gray-300 text-[11px] leading-relaxed">
                            {(tt.vatCountryMismatchBody || 'Il prefisso della P.IVA indica un cliente {detected}, ma il paese del cliente è attualmente {current}. Vuoi aggiornarlo?')
                              .replace('{detected}', (countries.find(c => c.code === vatDetectedCountry)?.name) || vatDetectedCountry)
                              .replace('{current}', (countries.find(c => c.code === country)?.name) || country)}
                          </p>
                          <button type="button"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      country: vatDetectedCountry,
                                      phone_prefix: getPrefixByCountry(vatDetectedCountry),
                                    }));
                                    rememberCountry(vatDetectedCountry);
                                    toast.success(`${tt.vatCountrySwitchedTo || 'Paese cliente aggiornato:'} ${(countries.find(c => c.code === vatDetectedCountry)?.flag) || ''} ${(countries.find(c => c.code === vatDetectedCountry)?.name) || vatDetectedCountry}`);
                                  }}
                                  className="mt-2 px-3 py-1.5 bg-lojack-clause-600 hover:bg-lojack-clause-700 rounded-lg text-white text-[11px] font-medium transition-colors"
                                  data-testid="dealer-vat-country-fix">
                            {(tt.vatCountrySwitchTo || 'Imposta paese su {detected}').replace('{detected}', (countries.find(c => c.code === vatDetectedCountry)?.flag || '') + ' ' + ((countries.find(c => c.code === vatDetectedCountry)?.name) || vatDetectedCountry))}
                          </button>
                        </div>
                      </div>
                    )}
                    {viesResult && (
                      <div className="mt-2 p-3 bg-blue-950/30 border border-blue-500/40 rounded-xl" data-testid="dealer-vies-result">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1">
                            <p className="text-blue-300 text-[10px] font-semibold uppercase tracking-wider mb-1">VIES (UE)</p>
                            <p className="text-white text-sm font-medium">{viesResult.name}</p>
                            {viesResult.address?.raw && (
                              <p className="text-gray-400 text-xs mt-1 whitespace-pre-line">{viesResult.address.raw}</p>
                            )}
                          </div>
                          <CheckCircle className="text-blue-400 flex-shrink-0" size={20} />
                        </div>
                        <div className="flex gap-2 mt-2">
                          <button type="button" onClick={applyViesData}
                                  className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-xs font-medium transition-colors"
                                  data-testid="dealer-vies-apply">
                            {tt.apply}
                          </button>
                          <button type="button" onClick={() => setViesResult(null)}
                                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 text-xs transition-colors"
                                  data-testid="dealer-vies-ignore">
                            {tt.ignore}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── STEP 4: Email ─────────────────────────────────────── */}
            <div className={sectionCls(enableEmail)}>
              <label className="text-gray-400 text-xs flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lojack-red text-white text-[10px] font-bold">4</span>
                {t.email}*
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={e => {
                  // Wipe the confirmation field whenever the primary
                  // email changes (Fabrizio anti-typo pattern).
                  if (e.target.value !== formData.email) setEmailConfirm('');
                  setFormData({...formData, email: e.target.value});
                }}
                disabled={!enableEmail}
                className={inputCls(enableEmail)}
                required
                data-testid="dealer-input-email"
              />

              {/* Email confirmation (anti-typo, Req. Fabrizio 22/02/2026).
                  Paste blocked → dealer must retype so cannot duplicate a
                  typo. Case-insensitive comparison. Field is disabled
                  until the primary email passes the basic regex. */}
              <div className="mt-2">
                <label className="text-gray-400 text-[11px] flex items-center gap-1.5">
                  <span className="text-lojack-red">↺</span>
                  {(tt.emailConfirmLabel) || 'Conferma email (ridigita)*'}
                </label>
                <input
                  type="email"
                  value={emailConfirm}
                  onChange={(e) => setEmailConfirm(e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  onDrop={(e) => e.preventDefault()}
                  onCopy={(e) => e.preventDefault()}
                  autoComplete="off"
                  disabled={!hasEmail}
                  placeholder={(tt.emailConfirmPlaceholder) || 'Ridigita l\u2019indirizzo email'}
                  className={`w-full mt-1 px-4 py-2.5 bg-[#111] border rounded-xl text-white focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed text-sm ${
                    !hasEmail || !emailConfirm ? 'border-gray-700' :
                    emailMatches ? 'border-emerald-500 focus:border-emerald-400' :
                                   'border-red-500 focus:border-red-400'
                  }`}
                  data-testid="dealer-input-email-confirm"
                />
                {hasEmail && emailConfirm && (
                  <p className={`mt-1 text-[11px] flex items-center gap-1.5 ${
                    emailMatches ? 'text-emerald-400' : 'text-red-400'
                  }`} data-testid="dealer-email-confirm-feedback">
                    {emailMatches
                      ? ((tt.emailConfirmOk) || '✓ Le email corrispondono')
                      : ((tt.emailConfirmMismatch) || '⚠ Le email non corrispondono')}
                  </p>
                )}
              </div>
            </div>

            {/* ── STEP 5: VIN ───────────────────────────────────────── */}
            <div className={sectionCls(enableVin)}>
              <label className="text-gray-400 text-xs flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lojack-red text-white text-[10px] font-bold">5</span>
                {t.vin}*
              </label>
              <input
                value={formData.vehicle_vin || ''}
                onChange={e => {
                  const raw = e.target.value;
                  const { canonical, replacements } = normaliseVin(raw);
                  // After auto-substitution, strip the 3 ISO-3779
                  // forbidden letters (I/O/Q) since real VINs don't
                  // contain them. This is the only "destructive" step.
                  const v = canonical.replace(/[IOQ]/g, '');
                  setFormData({...formData, vehicle_vin: v});
                  // Inform the dealer when we silently fixed a known
                  // OCR / paste artefact. Toast is short, info-tone,
                  // auto-dismissing — never amber/alarmist.
                  if (replacements.length > 0) {
                    const fixes = [...new Set(replacements.map(r => `${r.from} → ${r.to}`))].join(', ');
                    toast.info(`VIN normalizzato: ${fixes}`, { duration: 3500 });
                  }
                  // Trigger the WMI decoder as soon as we have at
                  // least the first 3 chars (the WMI). Cheap, offline,
                  // useful even for partial VINs.
                  if (v.length >= 3 && v !== lastDecodedVinRef.current) {
                    decodeVin(v);
                  }
                  if (v.length < 3) lastDecodedVinRef.current = '';
                }}
                disabled={!enableVin}
                maxLength={17}
                placeholder={`${egPrefix} 1HGCM82633A123456`}
                className={`${inputCls(enableVin)} uppercase font-mono tracking-wider`}
                required
                data-testid="dealer-input-vin"
              />
              <div className="flex items-center gap-2 mt-1">
                <p className="text-gray-500 text-xs flex-1">{(formData.vehicle_vin || '').length}/17 {vinDecoding && '• decoding…'}</p>
                {/* VIN validation status indicator — multi-state */}
                {vinValidation && !vinDecoding && (
                  <>
                    {vinValidation.severity === 'ok' && (
                      <span className="text-lojack-blue-500 text-xs flex items-center gap-1" data-testid="dealer-vin-status-ok">
                        <CheckCircle size={12} /> {tt.vinValidOk}
                      </span>
                    )}
                    {vinValidation.severity === 'warn' && vinValidation.code === 'OK_NON_NA' && (
                      <span className="text-lojack-blue-400 text-xs flex items-center gap-1" data-testid="dealer-vin-status-eu">
                        <CheckCircle size={12} /> {tt.vinValidEU}
                      </span>
                    )}
                    {vinValidation.severity === 'warn' && vinValidation.code === 'SUSPICIOUS_PATTERN' && (
                      <span className="text-lojack-clause-400 text-xs flex items-center gap-1" data-testid="dealer-vin-status-suspicious">
                        <AlertCircle size={12} /> {tt.vinSuspiciousRun}
                      </span>
                    )}
                    {vinValidation.severity === 'error' && (
                      <span className="text-red-400 text-xs flex items-center gap-1" data-testid="dealer-vin-status-error">
                        <AlertCircle size={12} />
                        {vinValidation.code === 'CHECKSUM_FAIL' ? tt.vinChecksumFail :
                         vinValidation.code === 'FORBIDDEN_CHAR' ? tt.vinForbiddenChar :
                         vinValidation.code === 'BAD_LENGTH' ? tt.vinBadLength :
                         vinValidation.message}
                      </span>
                    )}
                  </>
                )}
              </div>
              {/* Verbose details when checksum fails for NA VIN — shows the expected digit */}
              {vinValidation && vinValidation.severity === 'error' && vinValidation.code === 'CHECKSUM_FAIL' && (
                <p className="text-red-300/80 text-[11px] mt-1 ml-1" data-testid="dealer-vin-checksum-detail">
                  ↳ {tt.vinCheckDigitMismatch}: &quot;{vinValidation.details.expected}&quot; → &quot;{vinValidation.details.actual}&quot;
                </p>
              )}

              {/* VIN origin badge (WMI lookup) — informational hint only.
                  Shows the manufacturing country and (when known) the
                  brand the WMI maps to. Always rendered with neutral
                  styling: imported cars are normal in the European
                  aftermarket so we no longer flag "country mismatch".
                  See note on `vinOrigin` definition above. */}
              {vinOrigin && vinOrigin.confidence !== 'none' && (
                <div
                  className="mt-2 p-2.5 rounded-lg flex items-center gap-2 text-xs bg-[#0A0A0A] border border-gray-800"
                  data-testid="dealer-vin-origin"
                >
                  <Globe size={12} className="text-blue-400" />
                  <span className="text-gray-500 uppercase tracking-wider text-[10px]">
                    {tt.vehicleOriginLabel || 'Veicolo'}:
                  </span>
                  <span className="text-white font-medium truncate">
                    {/* Localise VIN-origin country to the dealer UI language.
                        `vinOrigin.country` is the static Italian endonym from
                        the WMI lookup table — falls back to it for non-EU
                        codes (US/JP/etc.) that getLocalizedCountryName cannot
                        resolve into Polish/etc. */}
                    {getLocalizedCountryName(vinOrigin.code, lang) || vinOrigin.country}
                    {vinOrigin.brand && (
                      <span className="text-gray-400 font-normal"> · {vinOrigin.brand}</span>
                    )}
                  </span>
                </div>
              )}
              {vinSuggestion && (
                <div className="mt-2 p-3 bg-blue-950/30 border border-blue-500/40 rounded-xl" data-testid="dealer-vin-suggestion">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-blue-300 text-[10px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1">
                        <Sparkles size={12} /> {vinSuggestion.auto_applied ? (tt.vinAutoApplied || 'Veicolo riconosciuto · compilato automaticamente') : tt.vinSuggestion}
                      </p>
                      <p className="text-white text-sm font-medium">
                        {[vinSuggestion.make, vinSuggestion.model, vinSuggestion.year].filter(Boolean).join(' • ')}
                      </p>
                      {vinSuggestion.auto_applied && (
                        <p className="text-gray-400 text-[11px] mt-1">{tt.vinEditable || 'I campi Marca e Modello sono comunque modificabili.'}</p>
                      )}
                    </div>
                    <CheckCircle className="text-blue-400 flex-shrink-0" size={20} />
                  </div>
                  {!vinSuggestion.auto_applied && (
                    <div className="flex gap-2 mt-2">
                      <button type="button" onClick={applyVinSuggestion}
                              className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-xs font-medium transition-colors"
                              data-testid="dealer-vin-apply">
                        {tt.apply}
                      </button>
                      <button type="button" onClick={() => setVinSuggestion(null)}
                              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 text-xs transition-colors"
                              data-testid="dealer-vin-ignore">
                        {tt.ignore}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── STEP 6: Vehicle (Plate + Brand + Model) ───────────── */}
            <div className={sectionCls(enableVehicle)}>
              <label className="text-gray-400 text-xs flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lojack-red text-white text-[10px] font-bold">6</span>
                {tt.vehicleData}*
              </label>
              <div className="space-y-3">
                <div>
                  <label className="text-gray-400 text-xs">{t.vrn}</label>
                  <input
                    value={formData.vehicle_plate || ''}
                    onChange={e => setFormData({...formData, vehicle_plate: e.target.value.toUpperCase()})}
                    disabled={!enableVehicle}
                    placeholder={`${egPrefix} AB123CD`}
                    className={`${inputCls(enableVehicle)} uppercase`}
                    data-testid="dealer-input-plate"
                  />
                  <p className="text-gray-600 text-[10px] mt-1">{tt.vrnOptional || 'Campo facoltativo'}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-400 text-xs">{t.vehicleBrand}*</label>
                    <input
                      value={formData.vehicle_brand}
                      onChange={e => setFormData({...formData, vehicle_brand: e.target.value})}
                      disabled={!enableVehicle}
                      className={inputCls(enableVehicle)}
                      required
                      data-testid="dealer-input-brand"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs">{t.vehicleModel}*</label>
                    <input
                      value={formData.vehicle_model}
                      onChange={e => setFormData({...formData, vehicle_model: e.target.value})}
                      disabled={!enableVehicle}
                      className={inputCls(enableVehicle)}
                      required
                      data-testid="dealer-input-model"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ── STEP 7: Service ───────────────────────────────────── */}
            <div className={sectionCls(enableService)}>
              <label className="text-gray-400 text-xs flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lojack-red text-white text-[10px] font-bold">7</span>
                {t.serviceType}*
              </label>
              <select
                value={formData.service_type}
                onChange={e => setFormData({...formData, service_type: e.target.value})}
                disabled={!enableService}
                className={inputCls(enableService)}
                data-testid="dealer-input-service"
              >
                {serviceTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value} style={{backgroundColor:'#111'}}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* ── STEP 8: Seller credited (Zorzutti 10/06/2026) ────────
                Mandatory dropdown that ties a customer to the Seller who
                actually closed the sale. Visible only for Manager and
                Back-Office roles — Sellers attribute customers to
                themselves automatically (the field is hidden + auto-set
                in the parent state). Step is enabled after Service so it
                appears as the natural last action before submit. */}
            {dealerRole !== 'seller' && (
              <SellerCreditedStep
                value={formData.assigned_seller_id || ''}
                onChange={(id) => setFormData({...formData, assigned_seller_id: id})}
                /* Mara Capelli feedback 12/02/2026: lo step 8 deve
                   essere disabilitato finché TUTTI gli step precedenti
                   (incluso brand+model) non sono completati. Il check
                   `Boolean(service_type)` da solo era inefficace perché
                   `service_type` è precompilato di default. */
                enabled={hasVehicle && hasVin && hasEmail && hasIdentity && hasPhone && Boolean(formData.service_type)}
                dealerToken={dealerToken}
                sectionCls={sectionCls}
                inputCls={inputCls}
                tt={tt}
              />
            )}

            {/* ── Submit / Reset / Esci ──
                Req. Mara Capelli Stand-up 26/05/2026 punto 1c: aggiungere
                sia Reset (svuota i campi senza uscire) sia Esci (torna
                alla dashboard del dealer con conferma se ci sono modifiche).
            */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(tt.exitConfirm)) {
                    if (onCancel) onCancel();
                    else navigate('/dealer');
                  }
                }}
                className="sm:w-32 py-3 border border-gray-700 rounded-xl text-gray-400 hover:bg-gray-800 transition-colors text-sm"
                data-testid="dealer-exit-form"
              >
                {tt.exit}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(tt.resetConfirm)) {
                    setFormData(initialFormData);
                    setShowConfirm(false);
                    setVinSuggestion(null);
                  }
                }}
                className="sm:w-32 py-3 border border-gray-700 rounded-xl text-gray-400 hover:bg-gray-800 transition-colors text-sm"
                data-testid="dealer-reset-form"
              >
                {tt.reset}
              </button>
              <button
                type="submit"
                disabled={creating || !enableSubmit}
                className="flex-1 btn-lojack flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="dealer-submit-customer"
              >
                {creating ? <><div className="spinner-small" /> {t.creating}</> : <><Send size={16} /> {t.verifyAndCreate}</>}
              </button>
            </div>
            {!enableSubmit && !creating && (
              <p className="text-gray-500 text-xs text-center">
                {tt.completeAllSteps}
              </p>
            )}
          </form>
        </div>
      </div>

      {/* CF CALCULATOR MODAL — Fabrizio 27/07/2026 */}
      <CFCalculatorModal
        open={showCFCalc}
        onClose={() => setShowCFCalc(false)}
        onUseCF={(cf) => {
          setFormData({ ...formData, tax_code: cf });
          toast.success(tt.cfCalcApplied || 'Codice Fiscale applicato');
        }}
        dealerToken={dealerToken}
        lang={wizardLang}
        prefillFirstName={formData.first_name || ''}
        prefillLastName={formData.last_name || ''}
      />

      {/* CONFIRM MODAL */}
      {showConfirm && (
        // Req. A. Zorzutti Stand-up 04/06/2026 pt. 10 — il click sul
        // backdrop NON deve chiudere la pop-up di riepilogo dati per
        // evitare la perdita inavvertita dei dati. Chiusura solo via X
        // o tasto "Modifica".
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          style={{width:'100vw',height:'100vh',top:0,left:0}}
          onClick={(e) => e.stopPropagation()}
          data-testid="dealer-confirm-overlay"
        >
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-lg border border-gray-700 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h2 className="text-lg font-bold text-white" data-testid="confirm-title">{t.confirmTitle}</h2>
              <button onClick={() => setShowConfirm(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <p className="text-gray-400 text-sm mb-4">{t.confirmSubtitle}</p>
              {(customerType === 'private' ? [
                [tt.customerType, tt.private],
                [t.firstName, formData.first_name],
                [t.lastName, formData.last_name],
                [cfInfo.label, formData.tax_code || '-'],
              ] : [
                [tt.customerType, tt.business],
                [tt.companyName, formData.company_name],
                [vatInfo.label, formData.vat_number || '-'],
              ]).concat([
                [t.email, formData.email],
                [t.phone, `${formData.phone_prefix || getPrefixByCountry(country)} ${formData.phone}`],
                [t.vin , formData.vehicle_vin || '-'],
                [t.vrn, formData.vehicle_plate || '-'],
                [t.brand, formData.vehicle_brand],
                [t.model, formData.vehicle_model],
                [t.country, countries.find(c => c.code === formData.country)?.name || formData.country],
                [t.service, serviceTypeOptions.find(o => o.value === formData.service_type)?.label || serviceTypeLabel(formData.service_type)],
              ]).map(([label, value]) => (
                <div key={label} className="flex justify-between items-center py-2 border-b border-gray-800">
                  <span className="text-gray-500 text-sm">{label}</span>
                  <span className="text-white text-sm font-medium text-right">{value}</span>
                </div>
              ))}
            </div>
            <div className="p-5 flex gap-3 border-t border-gray-700">
              <button onClick={() => setShowConfirm(false)} className="flex-1 py-2.5 px-4 border border-gray-600 rounded-xl text-gray-400 hover:bg-gray-800 transition-colors" data-testid="confirm-cancel">{t.edit}</button>
              <button onClick={handleCreateCustomer} disabled={creating} className="flex-1 btn-lojack flex items-center justify-center gap-2 disabled:opacity-50" data-testid="confirm-submit">{creating ? t.creating : t.confirmAndSend}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Seller-credited dropdown step (Zorzutti 10/06/2026)
// ─────────────────────────────────────────────────────────────────────
//
// Fetches the per-dealer dropdown options on mount and on every visible
// re-render of the form (the dropdown must auto-update when the Manager
// creates a new Seller without forcing the user to refresh — Q3 of the
// 10/06 stand-up). The Manager appears as the first entry by spec.

const SellerCreditedStep = ({ value, onChange, enabled, dealerToken,
                              sectionCls, inputCls, tt }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dealerToken) return undefined;
    let alive = true;
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/dealer/sellers/dropdown`,
          {
            cache: "no-store",
            headers: { Authorization: `Bearer ${dealerToken}` },
          },
        );
        if (!alive) return;
        const data = await res.json();
        if (Array.isArray(data)) setOptions(data);
      } catch (_) { /* network glitch — leave previous options */ }
      finally { if (alive) setLoading(false); }
    };
    fetchOptions();
    // Re-fetch when the tab regains focus — covers the case where the
    // Manager just created a new Seller in another tab.
    const onFocus = () => fetchOptions();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; window.removeEventListener("focus", onFocus); };
  }, [dealerToken]);

  return (
    <div className={sectionCls(enabled)} data-testid="dealer-seller-credited-step">
      <label className="text-gray-400 text-xs flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lojack-red text-white text-[10px] font-bold">8</span>
        {tt.sellerCreditedLabel || 'Venditore associato'}*
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={!enabled || loading}
        className={inputCls(enabled)}
        data-testid="dealer-input-assigned-seller"
      >
        <option value="" style={{backgroundColor:'#111'}}>
          {tt.sellerCreditedPlaceholder || 'Seleziona il venditore...'}
        </option>
        {options.map((o) => (
          <option key={o.id} value={o.id} style={{backgroundColor:'#111'}}>
            {o.label}{o.role === 'manager' ? ` — ${tt.sellerCreditedManagerSuffix || 'Titolare'}` : ''}
          </option>
        ))}
      </select>
      {options.length === 1 && (
        <p className="text-gray-500 text-xs italic">
          {tt.sellerCreditedHintNoSellers ||
           'Non hai ancora aggiunto venditori. Puoi attribuire questo cliente a te stesso oppure crearne uno dal tab "Venditori".'}
        </p>
      )}
    </div>
  );
};

export default DealerCustomerForm;

DealerCustomerForm.propTypes = {
  formData: PropTypes.object.isRequired,            // controlled-form state, must exist before render
  setFormData: PropTypes.func.isRequired,
  handleCreateCustomer: PropTypes.func.isRequired,
  creating: PropTypes.bool,
  showConfirm: PropTypes.bool,
  setShowConfirm: PropTypes.func,
  countries: PropTypes.array,                       // ISO list — empty array still renders correctly
  t: PropTypes.object.isRequired,                   // outer dealer translation pack (merged with wizard tf)
  lang: PropTypes.string,                           // 'IT'|'EN'|... — falls back to dealerCountry/IT
  dealerCountry: PropTypes.string,                  // defaults to 'IT' in destructure
  onCancel: PropTypes.func,
};
